import { ErrorHandler, Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable ,  of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Airport } from './models/airport.model';
import { Business } from '../business/models/business.model';
import { BusinessSummary } from './models/business-summary.model';
import { AirportAutocompleteResult } from '../shared/models/airport-autocomplete-result.model';
import { AirportSearchResult } from './models/airport-search-result.model';
import { LatLon } from '../util/geodesy/latlon';
import { BoundingBox } from '../util/geodesy/bounding-box';
import {
  ChangeType,
  TerminalProcedure,
  TerminalProcedureBatchFormat,
  TerminalProcedurePack
} from './models/terminal-procedure.model';
import { NOTAM } from './models/notam.model';


/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export const MIN_RADIUS = 25;


/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export const MAX_RADIUS = 500;


/**
 * Maximum area of bounding box (in square degrees) for airport bounding box search.
 */
export const MAX_BBOX_AREA = 150;


/**
 * File size limit for the terminal procedure batch script (100MB).
 */
export const MAX_PROC_BATCH_FILESIZE = 100e6;


/**
 * Maximum number of terminal procedures the user is allowed to download in a single batch.
 */
export const MAX_PROC_BATCH_LIMIT = 200;


@Injectable()
export class AirportService {
  /**
   * Airports cached by identifier.
   * @private
   */
  private _airportCache: Map<string, Airport> = new Map<string, Airport>();

  constructor(private _http: HttpClient,
              private _decimalPipe: DecimalPipe,
              private _errorHandler: ErrorHandler) {}

  /**
   * Returns a sanitized search term for airport search, with all invalid characters removed.
   * @param {string} term Original input term
   * @returns {string} Sanitized term
   */
  static sanitizeSearchTerm(term: string): string {
    return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
  }

  /**
   * Returns true when a term could be an airport ICAO/FAA identifier (as opposed to our internal numeric
   * airport id). FAA/ICAO identifiers always contain at least one letter, so an all-digit term is only ever
   * an internal id. The public search boxes use this to look up airports by identifier only — a user should
   * not be able to jump to an airport by typing its internal numeric id.
   * @param {string} term Original input term
   * @returns {boolean}
   */
  static isAirportIdentifier(term: string): boolean {
    const t = (term || '').trim();
    return t.length > 0 && !/^\d+$/.test(t);
  }

  /**
   * Returns true if we should show the fees section for the given business. This is based on a heuristic that
   * tries to determine if the given business is a traditional FBO that provides line services and/or fuel.
   * @param {Business | BusinessSummary} business
   * @returns {boolean}
   */
  static shouldShowFees(business: Business|BusinessSummary): boolean {
    if (business instanceof Business) {
      return (business.services && business.services.length > 0)
        || (business.overviewFees && business.overviewFees.length > 0)
        || (business.fuel && business.fuel.length > 0);
    }
    if (business instanceof BusinessSummary) {
      return business.hasOverviewFees || (business.fuel && business.fuel.length > 0);
    }
    return false;
  }

  /**
   * Gets an airport from the API by identifier
   * @param identifier {String} Airport identifier (FAA or ICAO)
   * @param [reload] {Boolean} Force reload airport (ignore cache)
   * @returns {Promise<Airport>} Airport promise
   */
  async getAirport(identifier: string, reload = false): Promise<Airport> {
    try {
      identifier = (identifier || '').trim().toUpperCase();
      if (!identifier) {
        return null;
      }
      if (!reload && this._airportCache.has(identifier)) {
        return this._airportCache.get(identifier);
      }
      const response = await this._http.get(`${environment.airportsApiUrl}/airports/${identifier}`).toPromise();
      const airport = Airport.parse(response);
      this._cacheAirport(airport);
      return airport;
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  private _cacheAirport(airport: Airport) {
    if (airport.icaoId) {
      this._airportCache.set(airport.icaoId, airport);
    }
    if (airport.faaId) {
      this._airportCache.set(airport.faaId, airport);
    }
  }

  /**
   * Returns airport search results for a given search term. The search term may match on airport identifier
   * (FAA or ICAO), name, or city.
   * @param term {string} Search term
   * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
   * @returns {Promise<AirportSearchResult[]>} List of matching airport results
   */
  async getAirportsBySearchTerm(term: string, includePrivate: boolean = false): Promise<AirportSearchResult[]> {
    term = AirportService.sanitizeSearchTerm(term);
    if (term.length < 3) {
      throw new Error('At least three alphanumeric characters are necessary to perform a search.');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/search/advanced`, {
        searchType : 'Basic',
        searchTerm : `%${term}%`,
        'private': includePrivate
      }).toPromise();
      return response.map(AirportSearchResult.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns airport search results for a comma separated list of airport IDs.
   * @param airportList {string} Search term
   * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
   * @returns {Promise<AirportSearchResult[]>} List of matching airport results
   */
  async getFavoriteAirports(airportList: string, includePrivate: boolean = false): Promise<AirportSearchResult[]> {
    if (airportList.length < 3) {
      throw new Error('At least three alphanumeric characters are necessary to perform a search.');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/search/advanced`, {
        searchType : 'Favorites',
        searchTerm : airportList,
        'private': includePrivate
      }).toPromise();
      return response.map(AirportSearchResult.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Performs a radial search for airports within a given distance of a center point.
   * @param latlon {LatLon} Center point of search
   * @param radius {number} Radius in nautical miles
   * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
   * @returns {Promise<AirportSearchResult[]>} List of matching airport results
   */
  async getAirportsByLatLon(latlon: LatLon, radius: number, includePrivate: boolean = false): Promise<AirportSearchResult[]> {
    if (!latlon) {
      throw new Error('latlon is required');
    }
    if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
      throw new Error(`radius is required and must be between ${MIN_RADIUS} and ${MAX_RADIUS} nautical miles.`);
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/search/advanced`, {
        searchType : 'Radius',
        latitude: latlon.lat,
        longitude: latlon.lon,
        radius,
        'private': includePrivate
      }).toPromise();
      return response.map(AirportSearchResult.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Performs a bounding box search for airports.
   * @param bounds {BoundingBox} Bounding box
   * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
   * @returns {Promise<AirportSearchResult[]>} List of matching airport results
   */
  async getAirportsByBoundingBox(bounds: BoundingBox, includePrivate: boolean = false): Promise<AirportSearchResult[]> {
    const area = bounds.getAreaDegrees();
    if (area > MAX_BBOX_AREA) {
      throw new Error(`Bounding box area (${area} square degrees) exceeds maximum (${MAX_BBOX_AREA}).`);
    }
    try {
      const neLat = bounds.ne.lat.toFixed(7);
      const neLon = bounds.ne.lon.toFixed(7);
      const swLat = bounds.sw.lat.toFixed(7);
      const swLon = bounds.sw.lon.toFixed(7);
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/search/advanced`, {
        searchType : 'Rectangle',
        searchTerm: `${neLat},${neLon},${swLat},${swLon}`,
        'private': includePrivate
      }).toPromise();
      return response.map(AirportSearchResult.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns all airports within a given state
   * @param state {string} State code
   * @param [includePrivate] {boolean} Whether to include private use airports in the results (defaults to false).
   * @returns {Promise<AirportSearchResult[]>} List of matching airport results
   */
  async getAirportsByState(state: string, includePrivate: boolean = false): Promise<AirportSearchResult[]> {
    if (!state) {
      throw new Error('"state" parameter is required when searching airports by state');
    }
    try {
      const response = await this._http.post<any[]>(`${environment.airportsApiUrl}/search/advanced`, {
        searchType : 'State',
        searchTerm: state,
        'private': includePrivate
      }).toPromise();
      return response.map(AirportSearchResult.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns terminal procedure data (current and next cycle) for a given array of airport FAA identifiers.
   * @param faaIds {Array<string>} FAA identifiers for the airports for which to return terminal procedures
   * @param [includeDeleted] {boolean} Whether to include deleted procedures in the results (defaults to false).
   * @returns {Promise<TerminalProcedurePack>} Terminal procedure data
   */
  async getTerminalProcedures(faaIds: Array<string>, includeDeleted: boolean = false): Promise<TerminalProcedurePack> {
    if (!faaIds || faaIds.length < 1) {
      return null;
    }
    try {
      const ids = faaIds.join(',');
      const response = await this._http.get<any>(`${environment.airportsApiUrl}/procedures?airports=${ids}`).toPromise();
      const result = TerminalProcedurePack.parse(response.terminalProcedures, response.terminalProceduresNext);
      if (!includeDeleted) {
        result.current.procedures = result.current.procedures.filter(p => p.changeType !== ChangeType.Deleted);
        result.next.procedures = result.next.procedures.filter(p => p.changeType !== ChangeType.Deleted);
      }
      return result;
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Returns a URL that can be used to download a set of terminal procedures, either as a single PDF or ZIP file.
   * @param procedures {Array<TerminalProcedure>} Terminal procedures to download
   * @param format {TerminalProcedureBatchFormat} Format
   * @returns {string} URL that can be used to download the procedure batch.
   */
  getProcedureBatchUrl(procedures: Array<TerminalProcedure>, format: TerminalProcedureBatchFormat): string {
    if (procedures.length < 1) {
      return null;
    }
    let script;
    switch (format) {
      case TerminalProcedureBatchFormat.ZIP:
        script = 'zipbatch';
        break;
      case TerminalProcedureBatchFormat.PDF:
        script = 'pdfbatch';
        break;
      default:
        throw new Error(`Unsupported instrument procedure file type: "${format}"`);
    }
    const procList = procedures.map(p => p.id).join(',');
    return `${environment.airportsApiUrl}/procedures/${script}?p=${procList}`;
  }

  /**
   * Gets the details of a business from the API by ID
   * @param id {String} Business ID
   * @returns {Promise<Business>} Business
   */
  async getBusiness(id: string): Promise<Business> {
    try {
      const response = await this._http.get(`${environment.airportsApiUrl}/businesses/${id}`).toPromise();
      return Business.parse(response);
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  /**
   * Gets airport autocomplete search results for the given query
   * @param query {String} Search query
   * @param [max] {number} max number to be returned by this service.  If API returns more records they will be removed.
   *                       Default is 10 records.
   * @returns {Observable<>} Airport promise
   */
  getAirportCompletions(query: string, max: number = 10): Observable<AirportAutocompleteResult[]> {
    query = (query || '').toString().replace(/[^a-z0-9 ,]+/gi, '').trim();
    if (query.length < 3) {
      return of([]);
    }
    try {
      return this._http
        .get<AirportAutocompleteResult[]>(`${environment.airportsApiUrl}/airportsautocomplete/${query}`)
        .pipe(
          map(d => d.map(r => AirportAutocompleteResult.parse(r)).slice(0, max))
        );
    } catch (e) {
      if (e && e.status === 404) {
        return null;
      }
      this._errorHandler.handleError(e);
      throw e;
    }
  }

  async getAirportNOTAMs(FaaId: string): Promise<NOTAM[]> {
    if (!FaaId) {
      throw new Error('FaaId is required');
    }
    try {
      const response = await this._http.get<any[]>(`${environment.airportsApiUrl}/airports/notams/${FaaId}`).toPromise();
      return response.map(NOTAM.parse);
    } catch (e) {
      this._errorHandler.handleError(e);
      throw e;
    }
  }
}
